import type { Stream } from '@zoom/videosdk';

import { normalizeError } from '../../../../../utils';
import { LocalScreenShareDelegator } from '../../../../core/room/participant/media/MediaDelegator';
import { MediaSource } from '../../../../core/room/participant/media/types';
import { handleError } from '../../../utils/handleError';

/**
 * Encapsulates Zoom's screen share functionality:
 * - Zoom tightly couples "publishing" with attaching to the element
 * -
 */
export class ZoomLocalScreenShareDelegator implements LocalScreenShareDelegator {
  readonly source = MediaSource.Screen as const;
  #stream: typeof Stream;
  #element: HTMLVideoElement | null = null;
  #handleAfterStart: () => void;
  #handleStartError: (error: Error) => void;

  constructor(stream: typeof Stream, handleAfterStart: () => void, handleError: (error: Error) => void) {
    this.#stream = stream;
    this.#handleAfterStart = handleAfterStart;
    this.#handleStartError = handleError;
  }

  async start(): Promise<void> {
    // No-op: Zoom requires an element to start sharing
    // The actual start happens in attach() when the element is available
    return;
  }

  async stop(): Promise<void> {
    if (!this.#element) {
      return;
    }

    try {
      await this.#stream.stopShareScreen();
      this.#element = null;
    } catch (detachError) {
      handleError('[ZoomLocalScreenShareDelegator]: detach', detachError);
    }

    return;
  }

  /**
   * Attaches the local screen share preview to a video element.
   * This triggers the browser picker and starts publishing to Zoom.
   */
  async attach(element: HTMLVideoElement): Promise<void> {
    if (element === this.#element) {
      return;
    }

    try {
      await this.#stream.startShareScreen(element);
      this.#handleAfterStart();
      this.#element = element;
    } catch (attachError) {
      handleError('[ZoomLocalScreenShareDelegator]: attach', attachError);
      this.#handleStartError(normalizeError(attachError));
    }
  }

  async detach(_element: HTMLVideoElement): Promise<void> {
    // no-op
  }
}
